import classNames from "classnames";
import { DateTime } from "luxon";
import { useContext, useState } from "react";
import { useHistory, useParams } from "react-router";
import { ClipLoader } from "react-spinners";
import {
	common_utils,
	RemoveVoid,
} from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { GuildListContext } from "../../../sesh_router";
import { trpc } from "../../../utils/trpc";
import { RemovingThrobber } from "../../removing_throbber";
import { DashboardContainer } from "../base_components/dashboard_container";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshSearchForm } from "../base_components/sesh_search_form";
import { useChannelName } from "../hooks/use_channel_name";
import { useDocTitle } from "../hooks/use_doc_title";
import { useIsAdmin } from "../hooks/use_is_admin";
import { useUserContext } from "../hooks/use_user_context";
import { DateTableData } from "../listings_form/event/event_listing_form/date_table_data";

import { ListingsFormTableData } from "../listings_form/listings_form_table_data";
import { PageButtons } from "../listings_form/page_buttons";
import MemberDisplayNameWithTooltip from "../member_display_name/member_display_name_with_tooltip";

type EventSortType = RemoveVoid<
	RouterInferenceHelpers["event"]["list"]["input"]
>["sort"];

function EditTimeAction(props: {
	event_id: string;
	start_time: Date;
	should_dm_users: boolean;
}) {
	let { server_id } = useParams<{ server_id: string }>();

	const trpc_context = trpc.useContext();

	const modify_event_time_mutation = trpc.event.modifyEventTime.useMutation({
		onSuccess: (data) => {
			trpc_context.event.getListItem.setData(
				(val) => {
					return {
						...val,
						start_time_str: data.start_time.toString(),
						end_time_str: data.end_time.toString(),
					};
				},
				{
					guild_id: server_id,
					message_id: props.event_id,
				}
			);
		},
	});

	if (modify_event_time_mutation.isLoading) {
		return (
			<span>
				<ClipLoader
					size={"1rem"}
					loading={true}
					color={Constants.EMBED_COLOR}
				/>
			</span>
		);
	}

	const decrement_disabled =
		props.start_time.getTime() - 1000 * 60 * 60 < new Date().getTime();

	return (
		<div className="flex gap-1">
			<SeshPrimaryButton
				className={classNames({ "input-disabled": decrement_disabled })}
				onClick={() => {
					if (decrement_disabled) {
						return;
					}
					modify_event_time_mutation.mutate({
						type: "Decrement",
						id: props.event_id,
						guild_id: server_id,
						should_dm_users: props.should_dm_users,
					});
				}}
			>
				-1 Hour
			</SeshPrimaryButton>
			<SeshPrimaryButton
				onClick={() =>
					modify_event_time_mutation.mutate({
						type: "Increment",
						id: props.event_id,
						guild_id: server_id,
						should_dm_users: props.should_dm_users,
					})
				}
			>
				+1 Hour
			</SeshPrimaryButton>
		</div>
	);
}

/**
 * NOTE: This is pasted from EventListingForm/PollListingForm. Ideally we should extract out shared components but its fine for now
 */
export default function EventListingFormWithDSTFix() {
	useDocTitle("DST fix");

	const { server_id } = useParams<{ server_id: string }>();

	const context = trpc.useContext();
	const guild_list = useContext(GuildListContext);
	const history = useHistory();

	const guild_data = guild_list?.find((val) => val.id === server_id);

	const [page, setPage] = useState(0);
	const [sort, setSort] = useState<EventSortType>({
		type: "Date",
		direction: "ASC",
	});
	const [should_dm_users, setShouldDMUsers] = useState(false);
	const [search, setSearch] = useState("");

	const list_query = trpc.event.list.useQuery(
		{
			guild_id: server_id,
			include_completed: false,
			cursor: page,
			sort,
			search,
		},
		{
			staleTime: 5000,
			keepPreviousData: true,
			onSuccess: (data) => {
				data.items.forEach((val) =>
					context.event.getListItem.setData(() => val, {
						guild_id: server_id,
						message_id: val.message_id,
					})
				);
			},
		}
	);

	if (!list_query.data || !guild_data) {
		return <>{DashboardUtils.RenderFullLoadingThrobberPage()}</>;
	}

	const query_items = list_query.data?.items;

	const disabled = list_query.isPreviousData;

	return (
		<DashboardContainer
			title={"DST Fix"}
			size="lg"
			bread_crumbs={
				<DashboardUtils.RenderLinkChain
					link_chain_data={[
						DashboardUtils.GetGuildSelectionLinkChainData(),
						DashboardUtils.RenderGuildOptionSelectLinkChain(
							guild_data
						),
						DashboardUtils.RenderEventSelectionLinkChain(
							guild_data
						),
					]}
				/>
			}
		>
			<div
				className={classNames("flex flex-col gap-2", {
					"input-disabled": disabled,
				})}
			>
				<div className="flex flex-col items-center gap-4 sm:flex-row">
					<SeshSearchForm
						onSubmit={setSearch}
						placeholder="Search by name"
					/>
					<div
						className={"ml-auto cursor-pointer"}
						onClick={
							disabled
								? undefined
								: () => setShouldDMUsers(!should_dm_users)
						}
					>
						<input
							type="checkbox"
							className="cursor-pointer"
							checked={should_dm_users}
							onChange={() => {}}
						/>{" "}
						DM Users on Start Time Modified
					</div>
				</div>
				<table
					className={classNames(
						"sesh-table listing-table responsive-table event-listings"
					)}
				>
					<thead>
						<tr>
							<th className="w-[27%]">
								<SortableTableHeader
									type="Date"
									current_sort={sort}
									setSort={setSort}
									is_loading={list_query.isPreviousData}
								/>
							</th>
							<th className="w-[23%]">
								<SortableTableHeader
									type="Name"
									current_sort={sort}
									setSort={setSort}
									is_loading={list_query.isPreviousData}
								/>
							</th>
							<th className="w-[12%]">
								<SortableTableHeader
									type="Channel"
									current_sort={sort}
									setSort={setSort}
									is_loading={list_query.isPreviousData}
								/>
							</th>
							<th className="w-[12%]">
								<SortableTableHeader
									type="Author"
									current_sort={sort}
									setSort={setSort}
									is_loading={list_query.isPreviousData}
								/>
							</th>
							<th className="w-[26%] overflow-hidden overflow-ellipsis">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{query_items && query_items.length > 0 ? (
							query_items.map((val) => {
								return (
									<EventRow
										key={val.message_id}
										message_id={val.message_id}
										should_dm_users={should_dm_users}
									/>
								);
							})
						) : (
							<tr className="">
								<td colSpan={6} className="text-center">
									No results found
								</td>
							</tr>
						)}
					</tbody>
				</table>
				<div className="flex w-full flex-wrap-reverse items-center gap-2">
					<SeshPrimaryButton
						className="w-fit self-end"
						onClick={() =>
							history.push(
								common_utils.getEventCreateURL(server_id)
							)
						}
					>
						<div className="flex items-center gap-2 px-2">
							<i className="fas fa-plus-circle"></i>
							<div>New Event</div>
						</div>
					</SeshPrimaryButton>
					<div className="ml-auto">
						{list_query.data && (
							<PageButtons
								page={page}
								total_items={
									list_query.data?.total_item_count ?? 0
								}
								setPage={setPage}
								is_loading={list_query.isPreviousData}
								items_per_page={
									Constants.LISTING_PAGINATION_LIMIT
								}
							/>
						)}
					</div>
				</div>
			</div>
		</DashboardContainer>
	);
}

const OppositeSortMap = {
	ASC: "DESC",
	DESC: "ASC",
} as const;

function SortableTableHeader(props: {
	type: EventSortType["type"];
	current_sort: EventSortType;
	setSort: (type: EventSortType) => void;
	is_loading?: boolean;
}) {
	const is_current_sort = props.current_sort.type === props.type;

	const is_pending_results = props.is_loading && is_current_sort;

	return (
		<div
			className={classNames("group flex flex-wrap items-center gap-1", {
				"cursor-pointer": !props.is_loading,
			})}
			onClick={() => {
				if (props.is_loading) {
					return;
				}
				props.setSort({
					type: props.type,
					direction: is_current_sort
						? OppositeSortMap[props.current_sort.direction]
						: "ASC",
				});
			}}
		>
			<div className="max-w-full overflow-hidden overflow-ellipsis">
				{props.type}
			</div>
			<span
				key={`${props.type}_${props.current_sort.direction}_${is_current_sort}_${is_pending_results}`}
			>
				<i
					className={classNames("group-hover:text-sesh-primary", {
						hidden: is_pending_results,
						"fas fa-sort text-base-light": !is_current_sort,
						"fas fa-sort-down":
							is_current_sort &&
							props.current_sort.direction === "DESC",
						"fas fa-sort-up":
							is_current_sort &&
							props.current_sort.direction === "ASC",
					})}
				/>
				{is_pending_results && <RemovingThrobber />}
			</span>
		</div>
	);
}

function getRepeatData(
	event: RouterInferenceHelpers["event"]["getListItem"]["output"]
) {
	const output_timezone = DateTime.local().zone.name;

	return event.rrule
		? {
				repeat_id: event.original_repeat_message_id,
				repeat_text: time_format_utils.formatRRule(
					event.rrule,
					output_timezone,
					DateTime.fromJSDate(new Date(event.start_time_str)),
					false
				),
		  }
		: undefined;
}

function EventRow(props: { message_id: string; should_dm_users: boolean }) {
	const { server_id } = useParams<{ server_id: string }>();

	const event_query = trpc.event.getListItem.useQuery(
		{
			guild_id: server_id,
			message_id: props.message_id,
		},
		{
			staleTime: Infinity,
		}
	);

	const channel_name = useChannelName(event_query.data?.channel_id);

	if (!event_query.isSuccess) {
		return <></>;
	}

	const event = event_query.data;

	const now_date = new Date();

	const start_time = new Date(event.start_time_str);
	const end_time = new Date(event.end_time_str);

	const is_in_past = end_time < now_date;

	const classString = is_in_past ? "opacity-75" : "";

	let emoji_data: JSX.Element;

	const repeat_data = getRepeatData(event);

	return (
		<tr className={classString}>
			<DateTableData
				start_time_js_date={start_time}
				end_time_js_date={end_time}
				repeat_data={repeat_data}
			/>
			<ListingsFormTableData content={event.name} />
			<ListingsFormTableData content={channel_name} />
			<td>
				<MemberDisplayNameWithTooltip
					user_id={event.author_id}
					tooltip_type="SeshTooltip"
				/>
			</td>
			<td>
				<RowActions
					guild_id={server_id}
					event={event}
					should_dm_users={props.should_dm_users}
				/>
			</td>
		</tr>
	);
}

function RowActions(props: {
	guild_id: string;
	event: RouterInferenceHelpers["event"]["getListItem"]["output"];
	should_dm_users: boolean;
}) {
	const [is_removing, setIsRemoving] = useState(false);

	const user = useUserContext();
	const is_admin_query = useIsAdmin();

	if (is_removing || !is_admin_query.isSuccess) {
		return (
			<div className="flex w-full flex-col items-center">
				<RemovingThrobber />
			</div>
		);
	}

	const is_event_creator = user?.id === props.event.author_id;
	const has_edit_event_perms = is_admin_query.data || is_event_creator;
	const has_event_started = new Date(props.event.start_time_str) < new Date();

	if (!has_edit_event_perms) {
		return <>Disabled due to invalid perms</>;
	}

	if (has_event_started) {
		return <>Disabled due to already started event</>;
	}

	return (
		<EditTimeAction
			event_id={props.event.message_id}
			start_time={new Date(props.event.start_time_str)}
			should_dm_users={props.should_dm_users}
		/>
	);
}
